/**
 * Drug names and substances come straight from the Polish registry and are
 * shown as-is in every language. Pharmaceutical forms are a closed vocabulary
 * (EDQM standard terms), so the English UI translates them with the map below
 * and falls back to the registry value for anything unknown.
 *
 * Keys are the exact `drugs.form` strings from the registry.
 */
export const DRUG_FORMS_EN: Readonly<Record<string, string>> = {
  Aerozol: 'Spray',
  'Aerozol do nosa': 'Nasal spray',
  'Aerozol do nosa, roztwór': 'Nasal spray, solution',
  'Aerozol do nosa, roztwór w pojemniku jednodawkowym':
    'Nasal spray, solution in single-dose container',
  'Aerozol do nosa, zawiesina': 'Nasal spray, suspension',
  'Aerozol do stosowania na skórę, roztwór': 'Cutaneous spray, solution',
  'Aerozol do stosowania w jamie ustnej': 'Oromucosal spray',
  'Aerozol do stosowania w jamie ustnej i gardle':
    'Oromucosal and oropharyngeal spray',
  'Aerozol do stosowania w jamie ustnej, roztwór': 'Oromucosal spray, solution',
  'Aerozol do uszu, roztwór': 'Ear spray, solution',
  'Aerozol inhalacyjny, roztwór': 'Pressurised inhalation, solution',
  'Aerozol inhalacyjny, zawiesina': 'Pressurised inhalation, suspension',
  'Aerozol na skórę': 'Cutaneous spray',
  'Aerozol na skórę, roztwór': 'Cutaneous spray, solution',
  'Aerozol na skórę, zawiesina': 'Cutaneous spray, suspension',
  'Aerozol podjęzykowy, roztwór': 'Sublingual spray, solution',
  'Aerozol przezskórny, roztwór': 'Transdermal spray, solution',
  'Aerozol wziewny, zawiesina': 'Pressurised inhalation, suspension',
  'Aerozol, roztwór': 'Spray, solution',
  Czopki: 'Suppositories',
  'Czopki doodbytnicze': 'Rectal suppositories',
  Drażetki: 'Coated tablets',
  'Dwa roztwory do sporządzania roztworu do infuzji':
    'Two solutions for solution for infusion',
  'Dyspersja do infuzji': 'Dispersion for infusion',
  'Dyspersja do nebulizacji': 'Nebuliser dispersion',
  'Dyspersja do wstrzykiwań': 'Dispersion for injection',
  'Dyspersja do wstrzykiwań o przedłużonym uwalnianiu':
    'Prolonged-release dispersion for injection',
  'Dyspersja do wstrzykiwań w ampułko-strzykawce':
    'Dispersion for injection in pre-filled syringe',
  Emulsja: 'Emulsion',
  'Emulsja do infuzji': 'Emulsion for infusion',
  'Emulsja do kąpieli': 'Bath emulsion',
  'Emulsja do wstrzykiwań': 'Emulsion for injection',
  'Emulsja do wstrzykiwań / do infuzji': 'Emulsion for injection/infusion',
  'Emulsja do wstrzykiwań / do infuzji w ampułko-strzykawce':
    'Emulsion for injection/infusion in pre-filled syringe',
  'Emulsja do wstrzykiwań lub infuzji': 'Emulsion for injection or infusion',
  'Emulsja na skórę': 'Cutaneous emulsion',
  Gaz: 'Gas',
  'Gaz do inhalacji': 'Inhalation gas',
  'Gaz i rozpuszczalnik do sporządzania dyspersji do wstrzykiwań / do infuzji':
    'Gas and solvent for dispersion for injection/infusion',
  'Gaz medyczny': 'Medicinal gas',
  'Gaz medyczny skroplony': 'Medicinal gas, liquefied',
  'Gaz medyczny skroplony, gaz medyczny sprężony':
    'Medicinal gas, liquefied / medicinal gas, compressed',
  'Gaz medyczny sprężony': 'Medicinal gas, compressed',
  'Generator radionuklidu': 'Radionuclide generator',
  Globulka: 'Pessary',
  Globulki: 'Pessaries',
  'Globulki dopochwowe': 'Vaginal pessaries',
  Granulat: 'Granules',
  'Granulat do przygotowania zawiesiny doustnej':
    'Granules for oral suspension',
  'Granulat do sporządzania roztworu doustnego': 'Granules for oral solution',
  'Granulat do sporządzania roztworu doustnego, w saszetce':
    'Granules for oral solution in sachet',
  'Granulat do sporządzania zawiesiny doustnej': 'Granules for oral suspension',
  'Granulat do sporządzania zawiesiny doustnej i stosowania w jamie ustnej':
    'Granules for oral and oromucosal suspension',
  'Granulat do sporządzania zawiesiny doustnej o przedłużonym uwalnianiu':
    'Prolonged-release granules for oral suspension',
  'Granulat do sporządzania zawiesiny doustnej w saszetce':
    'Granules for oral suspension in sachet',
  'Granulat dojelitowy': 'Gastro-resistant granules',
  'Granulat dojelitowy o przedłużonym uwalnianiu':
    'Gastro-resistant prolonged-release granules',
  'Granulat musujący': 'Effervescent granules',
  'Granulat o przedłużonym uwalnianiu': 'Prolonged-release granules',
  'Granulat o przedłużonym uwalnianiu, w saszetce':
    'Prolonged-release granules in sachet',
  'Granulat powlekany': 'Coated granules',
  'Granulat powlekany, w saszetce': 'Coated granules in sachet',
  'Granulat w kapsułce do otwierania': 'Granules in capsule for opening',
  'Granulat w saszetce': 'Granules in sachet',
  Granulki: 'Granules',
  'Granulki w pojemniku jednodawkowym': 'Granules in single-dose container',
  'Guma do żucia, lecznicza': 'Medicated chewing gum',
  Gąbka: 'Sponge',
  Implant: 'Implant',
  'Implant do ciała szklistego w aplikatorze':
    'Intravitreal implant in applicator',
  'Implant podskórny': 'Subcutaneous implant',
  'Implant w ampułko-strzykawce': 'Implant in pre-filled syringe',
  'Kapsułka dopochwowa, miękka': 'Vaginal capsule, soft',
  Kapsułki: 'Capsules',
  'Kapsułki do żucia, elastyczne': 'Chewable capsules, soft',
  'Kapsułki dojelitowe': 'Gastro-resistant capsules',
  'Kapsułki dojelitowe miękkie': 'Gastro-resistant capsules, soft',
  'Kapsułki dojelitowe, twarde': 'Gastro-resistant capsules, hard',
  'Kapsułki dopochwowe, miękkie': 'Vaginal capsules, soft',
  'Kapsułki dopochwowe, twarde': 'Vaginal capsules, hard',
  'Kapsułki elastyczne': 'Soft capsules',
  'Kapsułki miękkie': 'Soft capsules',
  'Kapsułki o przedłużonym uwalnianiu': 'Prolonged-release capsules',
  'Kapsułki o przedłużonym uwalnianiu, twarde':
    'Prolonged-release capsules, hard',
  'Kapsułki o zmodyfikowanym uwalnianiu': 'Modified-release capsules',
  'Kapsułki o zmodyfikowanym uwalnianiu, twarde':
    'Modified-release capsules, hard',
  'Kapsułki twarde': 'Hard capsules',
  'Koncentrat do sporządzania dyspersji do infuzji':
    'Concentrate for dispersion for infusion',
  'Koncentrat do sporządzania dyspersji do wstrzykiwań':
    'Concentrate for dispersion for injection',
  'Koncentrat do sporządzania emulsji do infuzji':
    'Concentrate for emulsion for infusion',
  'Koncentrat do sporządzania roztworu do infuzji':
    'Concentrate for solution for infusion',
  'Koncentrat do sporządzania roztworu do płukania gardła':
    'Concentrate for gargle',
  'Koncentrat do sporządzania roztworu do płukania jamy ustnej':
    'Concentrate for mouthwash',
  'Koncentrat do sporządzania roztworu do płukania jamy ustnej i gardła, roztwór doustny, do stosowania na skórę i do sporządzania inhalacji parowej':
    'Concentrate for mouthwash and gargle, oral solution, cutaneous solution and inhalation vapour',
  'Koncentrat do sporządzania roztworu do stosowania w jamie ustnej':
    'Concentrate for oromucosal solution',
  'Koncentrat do sporządzania roztworu do wstrzykiwań / do infuzji':
    'Concentrate for solution for injection/infusion',
  'Koncentrat do sporządzania roztworu do wstrzykiwań i infuzji':
    'Concentrate for solution for injection and infusion',
  'Koncentrat do sporządzania roztworu do wstrzykiwań lub infuzji':
    'Concentrate for solution for injection or infusion',
  'Koncentrat do sporządzania roztworu doustnego':
    'Concentrate for oral solution',
  'Koncentrat do sporządzania roztworu doustnego, do stosowania w jamie ustnej, gardła, na skórę':
    'Concentrate for oral, oromucosal, oropharyngeal and cutaneous solution',
  'Koncentrat do sporządzania roztworu doustnego, do stosowania w jamie ustnej, na skórę':
    'Concentrate for oral, oromucosal and cutaneous solution',
  'Koncentrat do sporządzania roztworu na skórę':
    'Concentrate for cutaneous solution',
  'Koncentrat do sporządzania zawiesiny do infuzji':
    'Concentrate for suspension for infusion',
  'Koncentrat i rozpuszczalnik do sporządzania roztworu do infuzji':
    'Concentrate and solvent for solution for infusion',
  'Koncentrat i rozpuszczalnik do sporządzania roztworu do wstrzykiwań':
    'Concentrate and solvent for solution for injection',
  Krem: 'Cream',
  'Krem doodbytniczy': 'Rectal cream',
  'Krem dopochwowy': 'Vaginal cream',
  Krople: 'Drops',
  'Krople do nosa': 'Nasal drops',
  'Krople do nosa, roztwór': 'Nasal drops, solution',
  'Krople do nosa, zawiesina': 'Nasal drops, suspension',
  'Krople do oczu': 'Eye drops',
  'Krople do oczu i uszu, zawiesina': 'Eye and ear drops, suspension',
  'Krople do oczu o przedłużonym uwalnianiu': 'Prolonged-release eye drops',
  'Krople do oczu, emulsja': 'Eye drops, emulsion',
  'Krople do oczu, roztwór': 'Eye drops, solution',
  'Krople do oczu, roztwór w pojemniku jednodawkowym':
    'Eye drops, solution in single-dose container',
  'Krople do oczu, zawiesina': 'Eye drops, suspension',
  'Krople do uszu': 'Ear drops',
  'Krople do uszu, roztwór': 'Ear drops, solution',
  'Krople do uszu, roztwór w pojemniku jednodawkowym':
    'Ear drops, solution in single-dose container',
  'Krople doustne': 'Oral drops',
  'Krople doustne, emulsja': 'Oral drops, emulsion',
  'Krople doustne, płyn': 'Oral drops, liquid',
  'Krople doustne, roztwór': 'Oral drops, solution',
  'Lakier do paznokci leczniczy': 'Medicated nail lacquer',
  'Lamelki podjęzykowe': 'Sublingual films',
  'Lamelki rozpadające się w jamie ustnej': 'Orodispersible films',
  'Lamelki ulegające rozpadowi w jamie ustnej': 'Orodispersible films',
  'Liofilizat do sporządzania roztworu do infuzji':
    'Lyophilisate for solution for infusion',
  'Liofilizat do sporządzania roztworu do wstrzykiwań':
    'Lyophilisate for solution for injection',
  'Liofilizat do sporządzania roztworu do wstrzykiwań, infuzji i inhalacji':
    'Lyophilisate for solution for injection, infusion and inhalation',
  'Liofilizat doustny': 'Oral lyophilisate',
  'Liofilizat podjęzykowy': 'Sublingual lyophilisate',
  'Matryca z klejem do tkanek': 'Sealant matrix',
  Maść: 'Ointment',
  'Maść do nosa': 'Nasal ointment',
  'Maść do oczu': 'Eye ointment',
  'Maść doodbytnicza': 'Rectal ointment',
  'Maść dopochwowa': 'Vaginal ointment',
  'Mikrowlewka doodbytnicza, roztwór': 'Rectal solution (micro-enema)',
  Pasta: 'Paste',
  'Pasta do zębów': 'Toothpaste',
  'Pasta doustna': 'Oral paste',
  'Pasta na skórę': 'Cutaneous paste',
  'Pastylka twarda': 'Lozenge',
  Pastylki: 'Lozenges',
  'Pastylki do ssania': 'Lozenges',
  'Pastylki miękkie': 'Pastilles',
  'Pastylki twarde': 'Lozenges',
  'Piana na skórę': 'Cutaneous foam',
  'Pianka doodbytnicza': 'Rectal foam',
  'Plaster do prób prowokacyjnych': 'Plaster for provocation test',
  'Plaster leczniczy': 'Medicated plaster',
  'Prekursor radiofarmaceutyczny, roztwór':
    'Radiopharmaceutical precursor, solution',
  'Prekursor radiofarmaceutyku, roztwór':
    'Radiopharmaceutical precursor, solution',
  Proszek: 'Powder',
  'Proszek do inhalacji': 'Inhalation powder',
  'Proszek do inhalacji w kapsułce twardej': 'Inhalation powder, hard capsule',
  'Proszek do inhalacji w kapsułkach twardych':
    'Inhalation powder, hard capsules',
  'Proszek do inhalacji, podzielony': 'Inhalation powder, pre-dispensed',
  'Proszek do nosa w pojemniku jednodawkowym':
    'Nasal powder in single-dose container',
  'Proszek do przygotowania koncentratu do sporządzania roztworu do infuzji':
    'Powder for concentrate for solution for infusion',
  'Proszek do sporządzania dyspersji do infuzji':
    'Powder for dispersion for infusion',
  'Proszek do sporządzania koncentratu do przygotowania roztworu do infuzji':
    'Powder for concentrate for solution for infusion',
  'Proszek do sporządzania koncentratu dyspersji do infuzji':
    'Powder for concentrate for dispersion for infusion',
  'Proszek do sporządzania koncentratu roztworu do infuzji':
    'Powder for concentrate for solution for infusion',
  'Proszek do sporządzania koncentratu roztworu do wstrzykiwań lub infuzji':
    'Powder for concentrate for solution for injection or infusion',
  'Proszek do sporządzania płynu do płukania gardła': 'Powder for gargle',
  'Proszek do sporządzania roztworu': 'Powder for solution',
  'Proszek do sporządzania roztworu do infuzji':
    'Powder for solution for infusion',
  'Proszek do sporządzania roztworu do infuzji dożylnych':
    'Powder for solution for intravenous infusion',
  'Proszek do sporządzania roztworu do infuzji dożylnych i do wstrzykiwań domięśniowych':
    'Powder for solution for intravenous infusion and intramuscular injection',
  'Proszek do sporządzania roztworu do infuzji dożylnych i roztworu doustnego':
    'Powder for solution for intravenous infusion and oral solution',
  'Proszek do sporządzania roztworu do infuzji i roztworu doustnego':
    'Powder for solution for infusion and oral solution',
  'Proszek do sporządzania roztworu do nebulizacji':
    'Powder for nebuliser solution',
  'Proszek do sporządzania roztworu do wstrzykiwań':
    'Powder for solution for injection',
  'Proszek do sporządzania roztworu do wstrzykiwań / do infuzji':
    'Powder for solution for injection/infusion',
  'Proszek do sporządzania roztworu do wstrzykiwań / do infuzji lub do pęcherza moczowego':
    'Powder for solution for injection/infusion or intravesical solution',
  'Proszek do sporządzania roztworu do wstrzykiwań / do infuzji lub roztworu doustnego':
    'Powder for solution for injection/infusion or oral solution',
  'Proszek do sporządzania roztworu do wstrzykiwań i infuzji':
    'Powder for solution for injection and infusion',
  'Proszek do sporządzania roztworu do wstrzykiwań lub infuzji':
    'Powder for solution for injection or infusion',
  'Proszek do sporządzania roztworu dopochwowego':
    'Powder for vaginal solution',
  'Proszek do sporządzania roztworu doustnego': 'Powder for oral solution',
  'Proszek do sporządzania roztworu doustnego w saszetce':
    'Powder for oral solution in sachet',
  'Proszek do sporządzania roztworu doustnego, proszek doustny':
    'Powder for oral solution, oral powder',
  'Proszek do sporządzania roztworu lub zawiesiny do wstrzykiwań':
    'Powder for solution or suspension for injection',
  'Proszek do sporządzania zawiesiny do infuzji':
    'Powder for suspension for infusion',
  'Proszek do sporządzania zawiesiny do podawania do pęcherza moczowego':
    'Powder for intravesical suspension',
  'Proszek do sporządzania zawiesiny do wstrzykiwań':
    'Powder for suspension for injection',
  'Proszek do sporządzania zawiesiny do wstrzykiwań lub roztworu do wstrzykiwań':
    'Powder for suspension for injection or solution for injection',
  'Proszek do sporządzania zawiesiny doustnej': 'Powder for oral suspension',
  'Proszek do sporządzania zawiesiny doustnej, w saszetce':
    'Powder for oral suspension in sachet',
  'Proszek do stosowania na skórę': 'Cutaneous powder',
  'Proszek dojelitowy i rozpuszczalnik do sporządzania zawiesiny doustnej':
    'Gastro-resistant powder and solvent for oral suspension',
  'Proszek doustny': 'Oral powder',
  'Proszek doustny lub do sporządzania zawiesiny doodbytniczej':
    'Oral powder or powder for rectal suspension',
  'Proszek doustny w kapsułkach otwieranych':
    'Oral powder in capsules for opening',
  'Proszek doustny w saszetce': 'Oral powder in sachet',
  'Proszek i podłoże żelowe do sporządzania żelu':
    'Powder and gel base for gel',
  'Proszek i rozpuszczalnik do przygotowania kleju do tkanek':
    'Powder and solvent for sealant',
  'Proszek i rozpuszczalnik do sporządzania koncentratu roztworu do infuzji':
    'Powder and solvent for concentrate for solution for infusion',
  'Proszek i rozpuszczalnik do sporządzania roztworu':
    'Powder and solvent for solution',
  'Proszek i rozpuszczalnik do sporządzania roztworu do infuzji':
    'Powder and solvent for solution for infusion',
  'Proszek i rozpuszczalnik do sporządzania roztworu do nebulizacji':
    'Powder and solvent for nebuliser solution',
  'Proszek i rozpuszczalnik do sporządzania roztworu do podawania do pęcherza moczowego':
    'Powder and solvent for intravesical solution',
  'Proszek i rozpuszczalnik do sporządzania roztworu do prób prowokacyjnych':
    'Powder and solvent for solution for provocation test',
  'Proszek i rozpuszczalnik do sporządzania roztworu do stosowania miejscowego':
    'Powder and solvent for solution for topical use',
  'Proszek i rozpuszczalnik do sporządzania roztworu do stosowania na skórę':
    'Powder and solvent for cutaneous solution',
  'Proszek i rozpuszczalnik do sporządzania roztworu do wstrzykiwań':
    'Powder and solvent for solution for injection',
  'Proszek i rozpuszczalnik do sporządzania roztworu do wstrzykiwań / do infuzji':
    'Powder and solvent for solution for injection/infusion',
  'Proszek i rozpuszczalnik do sporządzania roztworu do wstrzykiwań / do infuzji lub roztworu doustnego':
    'Powder and solvent for solution for injection/infusion or oral solution',
  'Proszek i rozpuszczalnik do sporządzania roztworu do wstrzykiwań do stosowania u dzieci':
    'Powder and solvent for solution for injection for paediatric use',
  'Proszek i rozpuszczalnik do sporządzania roztworu do wstrzykiwań i infuzji':
    'Powder and solvent for solution for injection and infusion',
  'Proszek i rozpuszczalnik do sporządzania roztworu do wstrzykiwań lub do infuzji':
    'Powder and solvent for solution for injection or infusion',
  'Proszek i rozpuszczalnik do sporządzania roztworu do wstrzykiwań lub infuzji':
    'Powder and solvent for solution for injection or infusion',
  'Proszek i rozpuszczalnik do sporządzania roztworu do wstrzykiwań podskórnych':
    'Powder and solvent for solution for subcutaneous injection',
  'Proszek i rozpuszczalnik do sporządzania roztworu do wstrzykiwań w ampułko-strzykawce':
    'Powder and solvent for solution for injection in pre-filled syringe',
  'Proszek i rozpuszczalnik do sporządzania zawiesiny do podawania do pęcherza moczowego':
    'Powder and solvent for intravesical suspension',
  'Proszek i rozpuszczalnik do sporządzania zawiesiny do pęcherza moczowego':
    'Powder and solvent for intravesical suspension',
  'Proszek i rozpuszczalnik do sporządzania zawiesiny do wstrzykiwań':
    'Powder and solvent for suspension for injection',
  'Proszek i rozpuszczalnik do sporządzania zawiesiny do wstrzykiwań o przedłużonym uwalnianiu':
    'Powder and solvent for prolonged-release suspension for injection',
  'Proszek i rozpuszczalnik do sporządzania zawiesiny do wstrzykiwań w ampułko-strzykawce':
    'Powder and solvent for suspension for injection in pre-filled syringe',
  'Proszek i rozpuszczalnik do sporządzania zawiesiny do wstrzykiwań śródksórnych':
    'Powder and solvent for suspension for intradermal injection',
  'Proszek i rozpuszczalnik do sporządzania zawiesiny o przedłużonym uwalnianiu do wstrzykiwań':
    'Powder and solvent for prolonged-release suspension for injection',
  'Proszek i roztwór do sporządzania roztworu do wstrzykiwań':
    'Powder and solution for solution for injection',
  'Proszek i zawiesina do sporządzania zawiesiny do wstrzykiwań':
    'Powder and suspension for suspension for injection',
  'Proszek krystaliczny': 'Crystalline powder',
  'Proszek musujący': 'Effervescent powder',
  'Proszek musujący i proszek do sporządzania zawiesiny doustnej':
    'Effervescent powder and powder for oral suspension',
  'Proszek musujący i zawiesina do sporządzania zawiesiny doustnej':
    'Effervescent powder and suspension for oral suspension',
  'Proszek, dyspersja i rozpuszczalnik do koncentratu do sporządzania dyspersji do infuzji':
    'Powder, dispersion and solvent for concentrate for dispersion for infusion',
  'Proszek, rozpuszczalnik i matryca do sporządzania matrycy do implantacji':
    'Powder, solvent and matrix for implantation matrix',
  Puder: 'Powder',
  'Puder leczniczy': 'Medicated powder',
  'Puder leczniczy do rozpylania na skórę': 'Cutaneous spray, powder',
  Płyn: 'Liquid',
  'Płyn do anestezji wziewnej': 'Inhalation vapour, liquid',
  'Płyn do inhalacji': 'Inhalation liquid',
  'Płyn do sporządzania inhalacji parowej': 'Inhalation vapour, liquid',
  'Płyn do stosowania na skórę': 'Cutaneous liquid',
  'Płyn do stosowania w jamie ustnej': 'Oromucosal liquid',
  'Płyn doustny': 'Oral liquid',
  'Płyn doustny i na skórę': 'Oral and cutaneous liquid',
  'Płyn doustny, płyn do stosowania w jamie ustnej, płyn na skórę':
    'Oral liquid, oromucosal liquid, cutaneous liquid',
  'Płyn doustny, płyn na skórę': 'Oral liquid, cutaneous liquid',
  'Płyn na skórę': 'Cutaneous liquid',
  'Płyn stomatologiczny': 'Dental liquid',
  'Płyn wziewny': 'Inhalation liquid',
  'Rozpuszczalnik do sporządzania leków parenteralnych':
    'Solvent for parenteral use',
  'Roztwory do sporządzania kleju do tkanek': 'Solutions for sealant',
  Roztwór: 'Solution',
  'Roztwór do dializy otrzewnowej': 'Solution for peritoneal dialysis',
  'Roztwór do dializy otrzewnowej, 4,25% glukozy i 1,75 mmol/l wapnia':
    'Solution for peritoneal dialysis, 4.25% glucose and 1.75 mmol/l calcium',
  'Roztwór do hemodializy / do hemofiltracji':
    'Solution for haemodialysis/haemofiltration',
  'Roztwór do hemofiltracji': 'Solution for haemofiltration',
  'Roztwór do hemofiltracji i hemodializy':
    'Solution for haemofiltration and haemodialysis',
  'Roztwór do infuzji': 'Solution for infusion',
  'Roztwór do infuzji dożylnych': 'Solution for intravenous infusion',
  'Roztwór do infuzji i roztwór doustny':
    'Solution for infusion and oral solution',
  'Roztwór do infuzji, preparat złożony':
    'Solution for infusion, combination product',
  'Roztwór do inhalacji': 'Inhalation solution',
  'Roztwór do modyfikowania frakcji krwi':
    'Solution for blood fraction modification',
  'Roztwór do nebulizacji': 'Nebuliser solution',
  'Roztwór do pęcherza moczowego': 'Intravesical solution',
  'Roztwór do płukania jamy ustnej': 'Mouthwash',
  'Roztwór do płukania jamy ustnej i gardła': 'Mouthwash and gargle',
  'Roztwór do skórnych prób punktowych': 'Solution for skin-prick test',
  'Roztwór do sporządzania kleju do tkanek': 'Solution for sealant',
  'Roztwór do stosowania doustnego i podjęzykowego':
    'Oral and sublingual solution',
  'Roztwór do stosowania na skórę': 'Cutaneous solution',
  'Roztwór do stosowania podjęzykowego': 'Sublingual solution',
  'Roztwór do stosowania w jamie ustnej': 'Oromucosal solution',
  'Roztwór do stosowania wewnątrzgałkowego': 'Intraocular solution',
  'Roztwór do wstrzykiwań': 'Solution for injection',
  'Roztwór do wstrzykiwań / do infuzji': 'Solution for injection/infusion',
  'Roztwór do wstrzykiwań / koncentrat do sporządzania roztworu do infuzji':
    'Solution for injection / concentrate for solution for infusion',
  'Roztwór do wstrzykiwań domięśniowych':
    'Solution for intramuscular injection',
  'Roztwór do wstrzykiwań dożylnych': 'Solution for intravenous injection',
  'Roztwór do wstrzykiwań i infuzji': 'Solution for injection and infusion',
  'Roztwór do wstrzykiwań lub infuzji': 'Solution for injection or infusion',
  'Roztwór do wstrzykiwań lub infuzji w ampułko-strzykawce':
    'Solution for injection or infusion in pre-filled syringe',
  'Roztwór do wstrzykiwań lub koncentrat do sporządzania roztworu do infuzji':
    'Solution for injection or concentrate for solution for infusion',
  'Roztwór do wstrzykiwań o przedłużonym uwalnianiu':
    'Prolonged-release solution for injection',
  'Roztwór do wstrzykiwań podskórnych': 'Solution for subcutaneous injection',
  'Roztwór do wstrzykiwań w ampułko-strzykawce':
    'Solution for injection in pre-filled syringe',
  'Roztwór do wstrzykiwań we wkładzie': 'Solution for injection in cartridge',
  'Roztwór do wstrzykiwań we wstrzykiwaczu':
    'Solution for injection in pre-filled pen',
  'Roztwór do wstrzykiwań, koncentrat do sporządzania roztworu do infuzji':
    'Solution for injection, concentrate for solution for infusion',
  'Roztwór doodbytniczy': 'Rectal solution',
  'Roztwór dopochwowy': 'Vaginal solution',
  'Roztwór doustny': 'Oral solution',
  'Roztwór doustny i doodbytniczy': 'Oral and rectal solution',
  'Roztwór doustny w pojemniku jednodawkowym':
    'Oral solution in single-dose container',
  'Roztwór doustny w saszetce': 'Oral solution in sachet',
  'Roztwór na skórę': 'Cutaneous solution',
  'Roztwór na skórę, roztwór do stosowania w jamie ustnej':
    'Cutaneous solution, oromucosal solution',
  'Roztwór, aerozol do stosowania zewnętrznego':
    'Solution, spray for external use',
  Syrop: 'Syrup',
  'Syrop w saszetce': 'Syrup in sachet',
  'System terapeutyczny domaciczny': 'Intrauterine delivery system',
  'System terapeutyczny dopochwowy': 'Vaginal delivery system',
  'System transdermalny': 'Transdermal system',
  'System transdermalny, plaster': 'Transdermal patch',
  Szampon: 'Shampoo',
  'Szampon leczniczy': 'Medicated shampoo',
  Tabletka: 'Tablet',
  'Tabletka dopochwowa': 'Vaginal tablet',
  'Tabletka i rozpuszczalnik do sporządzania kropli do oczu':
    'Tablet and solvent for eye drops',
  'Tabletka powlekana': 'Film-coated tablet',
  Tabletki: 'Tablets',
  'Tabletki do implantacji': 'Implantation tablets',
  'Tabletki do rozgryzania i żucia': 'Chewable tablets',
  'Tabletki do rozgryzania i żucia / do sporządzania zawiesiny':
    'Chewable/dispersible tablets',
  'Tabletki do sporządzania roztworu doustnego': 'Tablets for oral solution',
  'Tabletki do sporządzania zawiesiny doustnej': 'Tablets for oral suspension',
  'Tabletki do ssania': 'Lozenges',
  'Tabletki do żucia': 'Chewable tablets',
  'Tabletki dojelitowe': 'Gastro-resistant tablets',
  'Tabletki dopochwowe': 'Vaginal tablets',
  'Tabletki dozębodołowe': 'Periodontal tablets',
  'Tabletki drażowane': 'Coated tablets',
  'Tabletki musujące': 'Effervescent tablets',
  'Tabletki o przedłużonym działaniu': 'Prolonged-release tablets',
  'Tabletki o przedłużonym uwalnianiu': 'Prolonged-release tablets',
  'Tabletki o zmodyfikowanym uwalnianiu': 'Modified-release tablets',
  'Tabletki podjęzykowe': 'Sublingual tablets',
  'Tabletki podpoliczkowe': 'Buccal tablets',
  'Tabletki powlekane': 'Film-coated tablets',
  'Tabletki powlekane dojelitowe': 'Gastro-resistant film-coated tablets',
  'Tabletki powlekane o przedłużonym uwalnianiu':
    'Prolonged-release film-coated tablets',
  'Tabletki powlekane o zmodyfikowanym uwalnianiu':
    'Modified-release film-coated tablets',
  'Tabletki rozpuszczalne': 'Soluble tablets',
  'Tabletki ulegające rozpadowi w jamie ustnej': 'Orodispersible tablets',
  'Tkanka zastępcza żywa': 'Living tissue equivalent',
  'Zawiesina do implantacji': 'Suspension for implantation',
  'Zawiesina do infuzji': 'Suspension for infusion',
  'Zawiesina do nebulizacji': 'Nebuliser suspension',
  'Zawiesina do stosowania dotchawiczego i dooskrzelowego':
    'Endotracheopulmonary instillation, suspension',
  'Zawiesina do stosowania na zęby': 'Dental suspension',
  'Zawiesina do wstrzykiwań': 'Suspension for injection',
  'Zawiesina do wstrzykiwań o przedłużonym uwalnianiu':
    'Prolonged-release suspension for injection',
  'Zawiesina do wstrzykiwań o przedłużonym uwalnianiu w ampułko-strzykawce':
    'Prolonged-release suspension for injection in pre-filled syringe',
  'Zawiesina do wstrzykiwań w ampułko-strzykawce':
    'Suspension for injection in pre-filled syringe',
  'Zawiesina do wstrzykiwań w fiolce': 'Suspension for injection in vial',
  'Zawiesina do wstrzykiwań we wkładzie':
    'Suspension for injection in cartridge',
  'Zawiesina do wstrzykiwań we wstrzykiwaczu':
    'Suspension for injection in pre-filled pen',
  'Zawiesina doodbytnicza': 'Rectal suspension',
  'Zawiesina doustna': 'Oral suspension',
  'Zawiesina doustna i doodbytnicza': 'Oral and rectal suspension',
  'Zawiesina i emulsja do sporządzania emulsji do wstrzykiwań':
    'Suspension and emulsion for emulsion for injection',
  'Zawiesina i proszek do sporządzania zawiesiny do wstrzykiwań':
    'Suspension and powder for suspension for injection',
  'Zawiesina na skórę': 'Cutaneous suspension',
  'Zestaw do sporządzania preparatu radiofarmaceutycznego':
    'Kit for radiopharmaceutical preparation',
  'Zestaw dwóch roztworów do sporządzania wlewu dożylnego':
    'Kit of two solutions for intravenous infusion',
  Zioła: 'Herbal tea',
  'Zioła do zaparzania': 'Herbal tea',
  'Zioła do zaparzania w saszetkach': 'Herbal tea in sachets',
  'dyspersja do wstrzykiwań/infuzji': 'Dispersion for injection/infusion',
  'zawiesina i podłoże żelowe do sporządzania żelu':
    'Suspension and gel base for gel',
  Żel: 'Gel',
  'Żel do nosa': 'Nasal gel',
  'Żel do oczu': 'Eye gel',
  'Żel do podania dojelitowego': 'Intestinal gel',
  'Żel do stosowania na dziąsła': 'Gingival gel',
  'Żel do stosowania w jamie ustnej': 'Oromucosal gel',
  'Żel do szyjki macicy': 'Cervical gel',
  'Żel dojelitowy': 'Intestinal gel',
  'Żel doodbytniczy': 'Rectal gel',
  'Żel dopochwowy': 'Vaginal gel',
  'Żel okołozębowy': 'Periodontal gel',
  'Żel przezskórny': 'Transdermal gel',
};

/**
 * Pharmaceutical form for display in the current UI language. Polish (the
 * default) returns the registry value untouched; English uses the map above
 * and falls back to the registry value for unknown forms.
 */
export function formatDrugForm(
  language: string | undefined,
  form: string | undefined,
): string | undefined {
  if (!form) return form;
  if (!language?.toLowerCase().startsWith('en')) return form;
  return DRUG_FORMS_EN[form] ?? form;
}
